// Google Tag Manager dataLayer helper for the Vermittlungsportal.
//
// GTM triggers fire on the exact pushed string, so these MUST stay identical to
// what the container listens for. The briefing sheet's dataLayer code spelled
// the second event "setp2"; that was a typo and was corrected to "step2" on
// 20.08.2026. Renaming was safe because the container (GTM-WZCLPPTZ) held no
// trigger for either portal event at that point -- only register_praktikum_step1
// from the home site.
export const GTM_EVENTS = {
  registerPraktikumStep2: "register_praktikum_step2",
  registerPraktikumFinal: "register_praktikum_final",
} as const;

export interface DataLayerEvent extends Record<string, unknown> {
  event: string;
}

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

/**
 * Push a raw event onto the GTM dataLayer. Safe no-op if GTM never loaded
 * (e.g. a host with no configured GTM container): the array is created and the
 * event is simply buffered, no error is thrown.
 */
export function pushDataLayerEvent(payload: DataLayerEvent): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

export type PortalRole = "jugendlicher" | "bauernfamilie" | null;

/**
 * register_as: audience label the tracking team reports campaigns on. Labels are
 * their wording ("Jugendliche" / "Bauernfamilie"), not our internal role names.
 * Returns undefined when nobody is logged in -- the key is then left out
 * entirely rather than sent empty, so it never becomes a value of its own.
 */
export function registerAsLabel(
  role: PortalRole,
): "Jugendliche" | "Bauernfamilie" | undefined {
  if (role === "jugendlicher") return "Jugendliche";
  if (role === "bauernfamilie") return "Bauernfamilie";
  return undefined;
}

/**
 * Persistent dataLayer value, deliberately pushed WITHOUT an `event` key: it
 * merges into GTM's data model and stays readable by every later event, which is
 * what the tracking team asked for ("always available for reading" while logged
 * in). Pass null on logout to unset it again -- undefined is what clears a value
 * in the data model, so it is pushed as-is here and must not be stripped.
 */
export function setRegisterAs(role: PortalRole): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ register_as: registerAsLabel(role) });
}

export interface PraktikumRegistrationParams {
  farm_name: string;
  farm_kanton: string;
  booking_date: string;
  /** Omitted while nobody is logged in, see registerAsLabel(). */
  register_as?: string;
}

/**
 * Build the push payload. register_as is only added when it has a value, so the
 * key is absent rather than empty for visitors who are not logged in.
 */
function eventPayload(
  event: string,
  params: PraktikumRegistrationParams,
): DataLayerEvent {
  const payload: DataLayerEvent = {
    event,
    farm_name: params.farm_name,
    farm_kanton: params.farm_kanton,
    booking_date: params.booking_date,
  };
  if (params.register_as !== undefined) {
    payload.register_as = params.register_as;
  }
  return payload;
}

/** Registration intent: user clicks "Reservieren" on the calendar. */
export function trackRegisterPraktikumStep2(
  params: PraktikumRegistrationParams,
): void {
  pushDataLayerEvent(eventPayload(GTM_EVENTS.registerPraktikumStep2, params));
}

/** Conversion: a logged-in user successfully submits the reservation form. */
export function trackRegisterPraktikumFinal(
  params: PraktikumRegistrationParams,
): void {
  pushDataLayerEvent(eventPayload(GTM_EVENTS.registerPraktikumFinal, params));
}
